import { useState, useEffect } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, GitBranch, ListChecks } from 'lucide-react';
import { sequencesApi } from '../services/sequences.api';
import { toast } from 'sonner';

const CHANNELS = ['LinkedIn DM', 'Email', 'WhatsApp', 'Call', 'Other'];

function StepRow({ step, index, onChange, onRemove }) {
  return (
    <div className="flex items-start gap-2 bg-zinc-50 border border-zinc-200 rounded-md p-3">
      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-xs font-bold shrink-0 mt-0.5">
        {index + 1}
      </div>
      <div className="flex-1 grid grid-cols-3 gap-2">
        <div>
          <label className="block text-xs text-zinc-500 mb-1">Day offset</label>
          <input type="number" min={0} value={step.day_offset}
            onChange={e => onChange(index, 'day_offset', parseInt(e.target.value) || 0)}
            className="w-full text-sm border border-zinc-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
        </div>
        <div>
          <label className="block text-xs text-zinc-500 mb-1">Channel</label>
          <select value={step.channel} onChange={e => onChange(index, 'channel', e.target.value)}
            className="w-full text-sm border border-zinc-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white">
            {CHANNELS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="col-span-3">
          <label className="block text-xs text-zinc-500 mb-1">Template / Notes</label>
          <textarea rows={2} value={step.template_text}
            onChange={e => onChange(index, 'template_text', e.target.value)}
            placeholder="Message template or talking points..."
            className="w-full text-sm border border-zinc-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none" />
        </div>
      </div>
      <button onClick={() => onRemove(index)} className="text-zinc-300 hover:text-red-500 mt-0.5 transition-colors">
        <Trash2 size={14} />
      </button>
    </div>
  );
}

function CreateSequenceForm({ onCreated }) {
  const [name, setName] = useState('');
  const [steps, setSteps] = useState([{ day_offset: 0, channel: 'LinkedIn DM', template_text: '' }]);
  const [loading, setLoading] = useState(false);

  const addStep = () => setSteps(s => [...s, { day_offset: s.length, channel: 'LinkedIn DM', template_text: '' }]);
  const removeStep = (i) => setSteps(s => s.filter((_, idx) => idx !== i));
  const updateStep = (i, key, val) => setSteps(s => s.map((st, idx) => idx === i ? { ...st, [key]: val } : st));

  const submit = async () => {
    if (!name.trim()) { toast.error('Sequence name is required'); return; }
    setLoading(true);
    try {
      const { data } = await sequencesApi.create({ name, steps });
      toast.success(`Sequence "${data.name}" created`);
      onCreated(data);
      setName('');
      setSteps([{ day_offset: 0, channel: 'LinkedIn DM', template_text: '' }]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create sequence');
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-5 space-y-4">
      <h2 className="text-sm font-semibold text-zinc-800 flex items-center gap-2">
        <Plus size={14} className="text-indigo-500" /> New Sequence
      </h2>
      <div>
        <label className="block text-xs text-zinc-500 mb-1">Sequence Name</label>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. ICP-1 Cold Outreach"
          className="w-full text-sm border border-zinc-200 rounded-md px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Steps</p>
          <button onClick={addStep} className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
            <Plus size={12} /> Add Step
          </button>
        </div>
        {steps.map((step, i) => (
          <StepRow key={i} step={step} index={i} onChange={updateStep} onRemove={removeStep} />
        ))}
      </div>

      <button onClick={submit} disabled={loading}
        className="w-full text-sm bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium">
        {loading ? 'Saving...' : 'Create Sequence'}
      </button>
    </div>
  );
}

function SequenceCard({ seq }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
      <button className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-50 transition-colors"
        onClick={() => setOpen(o => !o)}>
        <div className="flex items-center gap-3">
          <GitBranch size={14} className="text-indigo-500" />
          <span className="text-sm font-medium text-zinc-800">{seq.name}</span>
          <span className="text-xs text-zinc-400 bg-zinc-100 px-2 py-0.5 rounded-full">{seq.steps?.length || 0} steps</span>
        </div>
        {open ? <ChevronUp size={14} className="text-zinc-400" /> : <ChevronDown size={14} className="text-zinc-400" />}
      </button>

      {open && (
        <div className="border-t border-zinc-100 px-4 py-3 space-y-2">
          {(seq.steps || []).map((step, i) => (
            <div key={i} className="flex items-start gap-3 text-xs">
              <div className="flex items-center justify-center w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 font-bold shrink-0">
                {i + 1}
              </div>
              <div className="flex-1">
                <span className="font-medium text-zinc-700">Day {step.day_offset}</span>
                <span className="mx-1.5 text-zinc-300">·</span>
                <span className="text-indigo-600">{step.channel}</span>
                {step.template_text && (
                  <p className="text-zinc-500 mt-0.5 line-clamp-2">{step.template_text}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Sequences() {
  const [sequences, setSequences] = useState([]);

  useEffect(() => {
    sequencesApi.list().then(r => setSequences(r.data)).catch(() => {});
  }, []);

  const onCreated = (seq) => setSequences(s => [seq, ...s]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <ListChecks size={20} className="text-indigo-500" />
        <div>
          <h1 className="text-lg font-semibold text-zinc-900">Outreach Sequences</h1>
          <p className="text-xs text-zinc-500">Define multi-step follow-up sequences and assign them to leads</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CreateSequenceForm onCreated={onCreated} />

        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-zinc-600 uppercase tracking-wide">
            Saved Sequences ({sequences.length})
          </h2>
          {sequences.length === 0 ? (
            <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-8 text-center">
              <GitBranch size={24} className="text-zinc-300 mx-auto mb-2" />
              <p className="text-sm text-zinc-400">No sequences yet. Create one to get started.</p>
            </div>
          ) : (
            sequences.map(seq => <SequenceCard key={seq.id} seq={seq} />)
          )}
        </div>
      </div>
    </div>
  );
}
